import { Component, Input, OnChanges, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { VTabsComponent } from "@app/directives/_layout/v-tabs/v-tabs.component";
import { VTabComponent } from "@directives/_layout/v-tabs/v-tab.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { IReportEditorSave } from "@reports/_components/report-editor/report-editor.component";
import { ReportSchedulesComponent } from "@reports/_components/report-schedules/report-schedules.component";
import { DynamicReportService } from "@reports/_services/dynamicReports.service";
import { IReportHistory } from "@reports/models/IDynamicReportLog";
import { IDynamicReportSchedule } from "@reports/models/IDynamicReportSchedule";
import { IReportDefinition } from "@reports/models/IReportDefinition";
import { NavigationService } from "@services/navigation.service";
import { copy } from "angular";
import { Subject } from "rxjs";
import { ReportHistoryService } from "../../_services/report-history.service";
import { ReportRunProgressState } from "../../enums/ReportRunProgressState.enum";
import { ReportsNavigationModule } from "../../reports.navigation";
import { ReportHistoryPageComponent } from "../report-history-page/report-history-page.component";
import "./report-view.component.scss";

@UntilDestroy()
@Component({
    selector: "report-view",
    template: require("./report-view.component.html")
})

export class ReportViewComponent implements OnInit, OnChanges {

    @Input() reportId: number;
    @Input() report: IReportDefinition;

    // ---

    history: IReportHistory[];
    lastReportRun: IReportHistory;
    activeSchedules: IDynamicReportSchedule[];

    selectedSchedule: IDynamicReportSchedule;
    reportToRun: IReportDefinition;

    loading = false;
    loadingReportHistory = false;
    runningReport = false;
    runningReportFinished = false;

    scheduleSaving = false;

    pdfUrl: string;

    reportEditorValid: boolean;
    showReportEditor: boolean;

    selectedHistory: IReportHistory;

    updateSubject = new Subject<void>();
    updateSubject$ = this.updateSubject.asObservable();

    requireSingleResource: boolean;

    errorDialog: any;

    selectedTab: VTabComponent;

    @ViewChild(ReportSchedulesComponent, { static: false }) reportSchedules: ReportSchedulesComponent
    @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;
    @ViewChild('tabs') tabs: VTabsComponent;
    @ViewChild('historyView') historyTab: VTabComponent;
    @ViewChild('reportView') viewTab: VTabComponent;
    @ViewChild('reportHistory') reportHistory: ReportHistoryPageComponent;

    constructor(
        private readonly dynamicReportService: DynamicReportService,
        private readonly reportHistoryService: ReportHistoryService,
        private readonly navigationService: NavigationService,
        private readonly domSanitizer: DomSanitizer
    ) {
    }

    ngOnInit(): void {
        this.checkForHistory();
        this.subscribeToReportHistory();
    }

    ngOnChanges(): void {
        this.reportId = parseInt(this.navigationService.getRouteParam("reportId"));
        this.loadData();
    }


    openReportRunner(): void {
        this.reportToRun = copy(this.report);
    }

    closeReportRunner() {
        this.reportToRun = null;
        this.runningReport = false;
        this.runningReportFinished = false;
        this.reportHistory?.reloadHistory();
    }

    onCreateScheduleClicked(): void {
        this.addSchedule();
    }

    onReportEditorValidated(valid: boolean) {
        this.reportEditorValid = valid;
    }

    onReportHistoryClicked(reportHistory: IReportHistory) {
        if (!reportHistory.pdfUrl) {
            console.warn("No pdf url provided for display");
            return;
        }

        this.openReportHistory(reportHistory);
    }

    onEditorSaved(reportEditorSave: IReportEditorSave): void {
        this.closeReportEditor();

        this.report = reportEditorSave.savedReport;

        if (reportEditorSave.savedAsCopy || reportEditorSave.deleted) {
            this.navigationService.go(ReportsNavigationModule.ParentRoot);
        }
    }

    onScheduleEditorSaved(savedSchedule: IDynamicReportSchedule): void {
        this.clearSelectedSchedule();

        this.reportSchedules?.scheduleSaved(savedSchedule);
        this.activeSchedules = this.reportSchedules?.schedules
    }

    onScheduleEditorDeleted(deletedSchedule: IDynamicReportSchedule): void {
        this.clearSelectedSchedule();

        this.reportSchedules?.scheduleDeleted(deletedSchedule);
        this.activeSchedules = this.reportSchedules?.schedules
    }

    onScheduleEditorCancelled(): void {
        this.clearSelectedSchedule();
    }

    openReportEditor() {
        this.showReportEditor = true;
    }

    closeReportEditor() {
        this.showReportEditor = false;
    }

    addSchedule(): void {
        this.selectSchedule(this.dynamicReportService.createReportScheduleTemplate(this.report.Id));
    }

    selectSchedule(schedule: IDynamicReportSchedule): void {
        this.selectedSchedule = copy(schedule);
        this.selectedSchedule.ReportDefinitionId = this.report.Id;
    }

    runReport(): void {
        // Fake timer for better UX
        this.loadingReportHistory = true;
        setTimeout(() => {
            this.loadingReportHistory = false;
            this.closeReportRunner();
            this.runningReport = true;
            this.tabs.selectTab(this.viewTab);
        }, 1_000);

        this.clearPdf();

        this.dynamicReportService
            .populateReport(this.report)
            .then(reportLog => {
                this.runningReport = false;
                this.runningReportFinished = true;
                this.selectHistory(reportLog);
            })
            .finally(() => {
                this.loadingReportHistory = false;
            });
    }

    viewLastRunReport() {
        this.runningReportFinished = false;
        const reportLog = this.selectedHistory;

        this.trustPdfUrl(reportLog.pdfUrl);
        this.selectHistory(reportLog);
        this.createHistory([...this.history, reportLog]);

        this.closeReportRunner();
    }

    showLatestReport(event: MouseEvent): void {
        event.preventDefault();

        this.loading = true;
        this.trustPdfUrl(this.lastReportRun.pdfUrl);
        this.selectHistory(this.lastReportRun);
    }

    viewHistory(): void {
        this.closeReportRunner();
        this.tabs.selectTab(this.historyTab);
        this.reportHistory?.reloadHistory();
    }

    reRunReport(): void {
        this.cancelError();
        this.runReport();
    }

    cancelError(): void {
        this.errorDialog.close();
    }

    onSelectedTabChanged(tab: VTabComponent): void {
        this.selectedTab = tab;
    }

    get filtersFormIsValid(): boolean {
        return !this.requireSingleResource ||
            this.requireSingleResource && (!!this.report.Parameters.VehicleId || !!this.report.Parameters.DriverId);
    }
    // ---

    private checkForHistory(): void {
        const reportHistoryId = this.navigationService.getRouteParam('historyId');
        if (!reportHistoryId) {
            return;
        }

        this.reportHistoryService.getSingleReportHistory(reportHistoryId).pipe(untilDestroyed(this)).subscribe(history => {
            this.openReportHistory(history);
            this.navigationService.clearRouteParam("historyId");
        })
    }

    private trustPdfUrl(pdfUrl: string) {
        this.pdfUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(pdfUrl) as any;
        this.loading = false;
    }

    private loadData(): Promise<any> {
        this.loading = true;

        this.clearData();
        this.clearUpdateSubject();

        const promises: Promise<any>[] = [];

        promises.push(this.loadReport());
        promises.push(this.loadReportSchedules(true));

        return Promise.all(promises).finally(() => {
            this.processReport(this.report);
            this.requireSingleResource = this.dynamicReportService.restrictReportToSingleResource(this.report);

            this.loading = false;
        });
    }

    private subscribeToReportHistory(): void {
        this.reportHistoryService.getReportHistory({ ReportDefinitionId: this.reportId }).pipe(
            untilDestroyed(this)
        ).subscribe(
            history => {
                this.createHistory(history);
            });
    }

    private openReportHistory(reportHistory: IReportHistory): void {
        if (reportHistory.ProgressState !== ReportRunProgressState.Finished) {
            this.runningReport = true;
        }

        this.trustPdfUrl(reportHistory.pdfUrl);
        this.selectHistory(reportHistory);
    }

    private createHistory(history: IReportHistory[]): void {
        this.history = history;
        this.processReportHistory(this.history);
    }

    private loadReport(): Promise<void> {
        return (this.dynamicReportService.getReports() as Promise<IReportDefinition[]>).then(reports => {
            this.report = reports?.find(report => report.Id == this.reportId);
        });
    }

    private loadReportSchedules(invalidateCache = false): Promise<void> {
        return (this.dynamicReportService.getReportSchedules({ invalidateCache: invalidateCache, IsActiveSchedule: true, ReportDefinitionId: this.reportId }) as Promise<IDynamicReportSchedule[]>).then(schedules => {
            this.activeSchedules = schedules;
        });
    }

    private processReport(report: IReportDefinition): void {
        this.updateNavigation(report);
    }

    private processReportHistory(reportHistory: IReportHistory[]): void {
        this.updateLastReportRun(reportHistory);
    }


    private updateNavigation(report: IReportDefinition): void {
        if (!report) {
            this.navigationService.go(ReportsNavigationModule.ParentRoot);
            return;
        }

        this.navigationService.updateBreadcrumbNameResolver({ reportNameKey: report.Title });
    }

    private updateLastReportRun(reportHistory: IReportHistory[]): void {
        if (!reportHistory?.length) {
            return;
        }

        this.lastReportRun = reportHistory.reduce((prev, current) => {
            return (prev.Id > current.Id && prev.ProgressState == ReportRunProgressState.Finished) ? prev : current
        });
    }

    private clearSelectedSchedule(): void {
        this.selectedSchedule = null;
    }

    private selectHistory(history: IReportHistory): void {
        this.selectedHistory = history;
    }

    private clearSelectedHistory(): void {
        this.selectedHistory = null;
    }

    private clearPdf(): void {
        delete this.pdfUrl;
    }

    private clearData(): void {
        this.report = null;
        this.history = null;
        this.activeSchedules = null;
    }

    private clearUpdateSubject(): void {
        this.updateSubject.next();
    }
}
